import {useCallback, useState} from 'react'
import {isAxiosError} from 'axios'
import i18next from 'i18next'
import {httpCreateBugReportIssue} from '../services/bugReporter.http'
import {useBugReporterStore} from '../store/useBugReporterStore'
import {AttachmentUploadState, BugReportAttachmentRef, BugReportFormValues, EnvironmentSnapshot} from '../types'

/**
 * The backend responds to every error with `{code, message}` (400 validation,
 * 502 upstream tracker failure). We surface `message` to the user verbatim — it
 * is meant for display and 502s proxy arbitrary upstream text no fixed i18n
 * table could cover — falling back to a generic string only when it's missing.
 */
const extractBackendMessage = (error: unknown): string => {
    if (isAxiosError(error)) {
        const message = error.response?.data?.message
        if (typeof message === 'string' && message.length > 0) return message
    }
    return i18next.t('errors:default')
}

export const useBugReporter = () => {
    const isOpen = useBugReporterStore(state => state.isOpen)
    const storeOpen = useBugReporterStore(state => state.open)
    const storeClose = useBugReporterStore(state => state.close)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)

    const open = useCallback(() => {
        setSubmitError(null)
        storeOpen()
    }, [storeOpen])

    const close = useCallback(() => {
        if (isSubmitting) return
        storeClose()
        setSubmitError(null)
    }, [isSubmitting, storeClose])

    const submit = useCallback(
        async (values: BugReportFormValues, snapshot: EnvironmentSnapshot, attachments: AttachmentUploadState[]) => {
            setIsSubmitting(true)
            setSubmitError(null)
            try {
                // Only the successfully uploaded files ride along with the issue; files
                // that failed to upload are dropped (auto-continue) and reported back so
                // the caller can tell the reporter which ones didn't attach.
                const uploaded: BugReportAttachmentRef[] = attachments
                    .filter(attachment => attachment.status === 'done' && attachment.assetUrl)
                    .map(attachment => ({
                        assetUrl: attachment.assetUrl as string,
                        filename: attachment.file.name,
                        contentType: attachment.file.type
                    }))
                const failedAttachments = attachments
                    .filter(attachment => attachment.status === 'error')
                    .map(attachment => attachment.file.name)

                const issue = await httpCreateBugReportIssue({
                    title: values.title,
                    description: values.description ?? '',
                    environment: snapshot,
                    attachments: uploaded
                })

                storeClose()
                setIsSubmitting(false)
                return {issue, failedAttachments}
            } catch (error) {
                setSubmitError(extractBackendMessage(error))
                setIsSubmitting(false)
                return null
            }
        },
        [storeClose]
    )

    return {isOpen, open, close, submit, isSubmitting, submitError}
}
